/**
 * Route Configuration
 *
 * Centralized configuration for public and protected routes.
 * This helps maintain consistency across the application and supports basePath.
 */

import { getBasePath } from "@/config/basePath";

/**
 * Base public routes (without basePath) - SINGLE SOURCE OF TRUTH
 * This array is imported and used by middleware, AuthGuard, and other components
 */
export const PUBLIC_ROUTES = [
  "/auth/login",
  "/auth/register",
  "/auth/signup",
  "/auth/forgot-password",
  "/auth/reset-password",
] as const;

/**
 * Get public routes with basePath support
 */
export function getPublicRoutes(): string[] {
  const basePath = getBasePath();
  const routes: string[] = [...PUBLIC_ROUTES];

  // Add basePath variants if basePath exists
  if (basePath) {
    PUBLIC_ROUTES.forEach((route) => {
      routes.push(`${basePath}${route}`);
    });
  }

  return routes;
}

/**
 * Get basePath-aware login URL
 */
export function getLoginUrl(): string {
  const basePath = getBasePath();
  return basePath ? `${basePath}/auth/login` : "/auth/login";
}

/**
 * Get basePath-aware dashboard URL
 */
export function getDashboardUrl(): string {
  const basePath = getBasePath();
  return basePath ? `${basePath}/` : "/";
}

/**
 * Client-side redirect helpers for edge cases where middleware doesn't handle it
 */
export const redirectHelpers = {
  toDashboard: () => {
    if (typeof window !== "undefined") {
      window.location.href = getDashboardUrl();
    }
  },
  toLogin: () => {
    if (typeof window !== "undefined") {
      window.location.href = getLoginUrl();
    }
  },
};

/**
 * Check if the current pathname is a public route
 * @param pathname - The current pathname to check
 * @returns true if the route is public, false otherwise
 */
export function isPublicRoute(pathname: string): boolean {
  const publicRoutes = getPublicRoutes();
  return publicRoutes.some(
    (route) => pathname === route || pathname.startsWith(route + "/"),
  );
}

/**
 * Check if the current pathname is a protected route
 * @param pathname - The current pathname to check
 * @returns true if the route is protected, false otherwise
 */
export function isProtectedRoute(pathname: string): boolean {
  return !isPublicRoute(pathname);
}
